<script setup lang="ts">
import { ref, onBeforeMount, computed } from "vue";
import { debounce } from "vue-debounce";
import MainBreadcrumbs from "@/ui/components/header/MainBreadcrumbs.vue";
import { useApiClient } from "@/api/client";
import { useRoute } from "vue-router";
import RuleModelVersionDTO = App.Data.RuleEngine.RuleModelVersionDTO;
import useDialog from "@/ui/composables/useDialog";
import { toastApiResult } from "@/toaster";
import dayjs from "dayjs";
import router from "@/router/router";
import { useBaseStore } from "@/stores/base";
import RuleModelVersionMinimalDTO = App.Data.RuleEngine.RuleModelVersionMinimalDTO;

const breadcrumbs = ref([
	{
		title: "Rule Based Engine",
		disabled: false,
		to: "/rule-engine",
	},
]);

const model_uuid: string | null =
	useRoute().params.model_uuid !== ""
		? String(useRoute().params.model_uuid)
		: null;

const completeBreadCrumbs = async () => {
	const response = await useApiClient("ruleEngine").showModelMeta(model_uuid);
	breadcrumbs.value.push({
		title: response.isOk() ? response.value.name : model_uuid,
		disabled: true,
		to: `/rule-engine/versions/${model_uuid}`,
	});
};

onBeforeMount(() => {
	completeBreadCrumbs();
});

const itemsPerPage = ref<number>(100);

const headers = [
	{ title: "Name", key: "name", sortable: true },
	{ title: "Uuid", key: "uuid", sortable: true },
	{ title: "Created By", key: "created_by_user", sortable: false },
	{ title: "Created At", key: "created_at", sortable: false },
	{ title: "Active Version", key: "is_active", sortable: false },
	{ title: "Actions", key: "actions", width: "150", align: "end" },
];

const isLoading = ref<boolean>(false);
const items = ref<RuleModelVersionMinimalDTO[]>([]);
const totalItemCount = ref<number>(0);
const page = ref<number>(1);
const sortBy = ref([]);

const loadItems = debounce(async ({ page, itemsPerPage, sortBy }) => {
	isLoading.value = true;

	const client = useApiClient("ruleEngine");

	const response = await client.versionIndex(model_uuid, {
		page,
		itemsPerPage,
		sortBy,
	});

	if (response.isOk()) {
		items.value = response.value;
		totalItemCount.value = response.value.length;
	}

	isLoading.value = false;
}, 300);

/**
 * Publishing takes effect immediately and there is no undo, so the prompt names
 * the version and what it replaces rather than asking a generic "are you sure".
 */
const makeActiveVersion = async (ruleModelVersion: RuleModelVersionDTO) => {
	const current = items.value.find((version) => version.is_active);

	const replaces = current
		? `This replaces “${current.name}”, which becomes retired.`
		: `This model currently has no active version.`;

	const confirm = await useDialog().confirm({
		title: `Publish “${ruleModelVersion.name}”?`,
		message: `“${ruleModelVersion.name}” will start being used for this model straight away. ${replaces}`,
	});
	if (!confirm) return;

	const response = await toastApiResult(
		useApiClient("ruleEngine").makeActiveVersion(
			model_uuid,
			ruleModelVersion.uuid
		)
	);

	if (response.isOk()) {
		loadItems({
			page: page.value,
			itemsPerPage: itemsPerPage.value,
			sortBy: sortBy.value,
		});
	}
};

const selectedDraftVersions = ref([]);

/** Server-side and permanent — unlike edits in the builder, this has no undo. */
const destroyVersions = async () => {
	const names = selectedDraftVersions.value
		.map((version) => `“${version.name}”`)
		.join(", ");

	const confirm = await useDialog().confirm({
		title:
			selectedDraftVersions.value.length === 1
				? `Delete ${names}?`
				: `Delete ${selectedDraftVersions.value.length} draft versions?`,
		message: `${names} will be permanently deleted. This cannot be undone.`,
		isDelete: true,
	});
	if (!confirm) return;

	const response = await toastApiResult(
		useApiClient("ruleEngine").destroyVersions(
			model_uuid,
			selectedDraftVersions.value.map((draftVersion) => draftVersion.uuid)
		)
	);

	if (response.isOk()) {
		loadItems({
			page: page.value,
			itemsPerPage: itemsPerPage.value,
			sortBy: sortBy.value,
		});
	}
};

const isDeleteAvailable = computed(() =>
	selectedDraftVersions.value.every((v) => !v.is_active && !v.was_active)
);

/**
 * Says why an action is unavailable instead of leaving a greyed-out button with
 * no explanation.
 */
const deleteBlockedReason = computed(() => {
	if (selectedDraftVersions.value.length === 0)
		return "Select drafts to delete";
	if (!isDeleteAvailable.value) {
		return "Active and retired versions cannot be deleted";
	}
	return null;
});

const compareBlockedReason = computed(() => {
	if (selectedDraftVersions.value.length < 2) {
		return "Select 2 or 3 versions to compare";
	}
	if (selectedDraftVersions.value.length > 3) {
		return "Compare at most 3 versions at a time";
	}
	return null;
});

const compareVersions = () => {
	router.push({
		name: "pricingEngine.versions.compare",
		params: { model_uuid },
		query: {
			versions: selectedDraftVersions.value.map(
				(draftVersion) => draftVersion.uuid
			),
		},
	});
};

const userCanPublishRuleModel = computed(() => {
	return useBaseStore().currentUser?.derivedPermissions.includes(
		"can_publish_rule_model"
	);
});
</script>

<template>
	<main-breadcrumbs :breadcrumbs="breadcrumbs" />

	<v-card>
		<v-card-text>
			<v-data-table-server
				v-model="selectedDraftVersions"
				v-model:items-per-page="itemsPerPage"
				v-model:page="page"
				v-model:sort-by="sortBy"
				:headers="headers"
				:items="items"
				:items-length="totalItemCount"
				:loading="isLoading"
				@update:options="loadItems"
				return-object
				show-select
			>
				<template v-slot:item.name="{ item }">
					<router-link
						class="router-link-primary"
						:to="{
							name: 'pricingEngine.builder',
							params: {
								version_uuid: item.uuid,
								model_uuid: item.rule_model_uuid,
							},
						}"
					>
						{{ item.name }}
					</router-link>
				</template>
				<template v-slot:item.uuid="{ item }">
					<v-chip
						v-tooltip="`${item.uuid}`"
						density="compact"
						variant="tonal"
						color="on-surface"
					>
						...{{ item.uuid.slice(-5) }}
					</v-chip>
				</template>
				<template v-slot:item.created_by_user="{ item }">
					{{ item.created_by_user?.name ?? "Automation" }}
				</template>
				<template v-slot:item.created_at="{ item }">
					{{ dayjs(item.created_at).format("MMMM D, YYYY - HH:mm:ss") }}
				</template>
				<template v-slot:item.is_active="{ item }">
					<v-chip v-if="item.is_active" color="primary" density="compact">
						<v-icon size="15" icon="fa: fa-light fa-check" start></v-icon>
						Active
					</v-chip>
					<v-chip
						v-else-if="item.was_active"
						density="compact"
						variant="tonal"
						color="on-surface"
					>
						Retired
					</v-chip>
					<v-chip v-else variant="tonal" color="on-surface" density="compact"
						>Draft</v-chip
					>
				</template>
				<template v-slot:item.actions="{ item }">
					<v-btn
						v-if="userCanPublishRuleModel"
						v-tooltip="`Use this version of the model`"
						icon
						variant="text"
						:disabled="item.is_active"
						@click="makeActiveVersion(item)"
						density="compact"
					>
						<v-icon size="18">fa: fa-duotone fa-rotate</v-icon>
					</v-btn>
					<span v-else>-</span>
				</template>
				<template #footer.prepend>
					<div class="d-flex align-center">
						<v-btn
							variant="tonal"
							prepend-icon="fa: fa-light fa-trash-alt"
							color="primary"
							:disabled="!!deleteBlockedReason"
							v-tooltip="deleteBlockedReason ?? 'Delete the selected drafts'"
							@click="destroyVersions()"
						>
							Delete drafts
						</v-btn>
						<v-btn
							class="ml-3"
							:disabled="!!compareBlockedReason"
							color="indigo"
							variant="tonal"
							v-tooltip="
								compareBlockedReason ?? 'Compare the selected versions'
							"
							@click="compareVersions"
						>
							Compare versions
						</v-btn>

						<span class="ml-4 selection-hint">
							{{
								deleteBlockedReason && compareBlockedReason
									? compareBlockedReason
									: `${selectedDraftVersions.length} selected`
							}}
						</span>
					</div>
					<v-spacer />
				</template>
			</v-data-table-server>
		</v-card-text>
	</v-card>
</template>

<style scoped lang="scss">
.selection-hint {
	font-size: 0.8rem;
	opacity: 0.6;
}
</style>
